import React, { useCallback } from 'react';

import { useThemedStyles } from '../../../hooks/useThemedStyles';
import { Text } from '../../../components';
import { PlaceSelector, MachineSelector, PlanogramSelector, RouteSelector } from '../../../components/selector';
import SetupSection from '../components/SetupSection';
import SetupInfoRow from '../components/SetupInfoRow';
import SetupSwitchField from '../components/SetupSwitchField';
import SetupPickerField from '../components/SetupPickerField';
import SetupTextField from '../components/SetupTextField';
import SetupAssigneePicker from '../components/SetupAssigneePicker';
import SetupPhotoSection from '../components/SetupPhotoSection';

import { flattenObjectDataInArray } from '../../../../common/utils/general/flattenObjectData';
import {
	getMachineSetupCardPaymentOnlyChoice,
	getMachineSetupPaymentRequirements,
	MACHINE_SETUP_CARD_PAYMENT_ONLY_HINT,
	MACHINE_SETUP_CARD_PAYMENT_ONLY_OPTIONS,
} from '../../../../common/utils/machine/machineSetupPayments';

import {
	MACHINE_DOOR_ANGLE,
	MACHINE_DOOR_ANGLE_NAME,
	MACHINE_QUALITY,
	MACHINE_QUALITY_NAME,
	MACHINE_SETUP_PREFERENCE_VALUE,
	MACHINE_SETUP_PREFERENCE_VALUE_NAME,
	MACHINE_SIZE,
	MACHINE_SIZE_NAME,
	YES_NO_VALUE,
	YES_NO_VALUE_NAME,
	POS_TYPE,
} from '../../../../common/constants';
import { getIdOrNull } from './MachineSetupDetailHelpers';
import { createMachineSetupDetailStyles } from './MachineSetupDetail.styles';

const MachineSetupInformationSection = ({
	machineSetup,
	assignedMachine,
	cardPaymentOnlyDisplay,
	dueDateLabel,
	healthComplianceLabel,
	updateMachineSetup,
	addPhoto,
	handleUpdateMediaComment,
}) => {
	const styles = useThemedStyles(createMachineSetupDetailStyles);
	const cardPaymentOnlyChoice = getMachineSetupCardPaymentOnlyChoice(machineSetup);
	const cardPaymentOnlyHint = cardPaymentOnlyChoice
		? MACHINE_SETUP_CARD_PAYMENT_ONLY_HINT
		: `Current requirements: ${cardPaymentOnlyDisplay}. Select Yes or No to replace this custom configuration.`;

	const handleAssigneeChange = useCallback((ids) => {
		updateMachineSetup({ assignees: flattenObjectDataInArray(ids) });
	}, [updateMachineSetup]);

	const updateMachineQuality = useCallback((key, value) => {
		updateMachineSetup({
			machineQualityObject: {
				...(machineSetup.machineQualityObject || {}),
				[key]: value,
			},
		});
	}, [machineSetup.machineQualityObject, updateMachineSetup]);

	const updateMachineSize = useCallback((key, value) => {
		updateMachineSetup({
			machineSizeObject: {
				...(machineSetup.machineSizeObject || {}),
				[key]: value,
			},
		});
	}, [machineSetup.machineSizeObject, updateMachineSetup]);

	const handlePlaceChange = useCallback(place => updateMachineSetup({ place: getIdOrNull(place) }), [updateMachineSetup]);
	const handleMachineChange = useCallback(machine => {
		const machineId = getIdOrNull(machine);
		return updateMachineSetup({
			machine: machineId,
			...(assignedMachine?.id !== machineId ? { commDevice: null } : {}),
		});
	}, [assignedMachine?.id, updateMachineSetup]);
	const handleRouteChange = useCallback(route => updateMachineSetup({ route: getIdOrNull(route) }), [updateMachineSetup]);
	const handlePlanogramChange = useCallback(planogram => updateMachineSetup({ planogram: getIdOrNull(planogram) }), [updateMachineSetup]);
	const handleCardPaymentOnlyChange = useCallback(choice => {
		const requirements = getMachineSetupPaymentRequirements(choice);
		if (requirements) {
			updateMachineSetup(requirements);
		}
	}, [updateMachineSetup]);

	return (
		<SetupSection title="Information">
			<Text style={styles.sectionLabel}>Basic information</Text>
			<SetupInfoRow
				label="Identifier"
				value={machineSetup.identifier}
			/>
			<SetupInfoRow
				label="Location setup"
				value={machineSetup.locationSetup?.identifier}
			/>
			<SetupInfoRow
				label="Due date"
				value={dueDateLabel}
			/>
			<SetupInfoRow
				label="Location"
				value={machineSetup.locationSetup?.location?.name}
			/>
			<Text style={styles.fieldLabel}>Place</Text>
			<PlaceSelector
				initialValue={machineSetup.place}
				value={machineSetup.place}
				onChange={handlePlaceChange}
				disabled={!assignedMachine}
				location={machineSetup.locationSetup?.location}
			/>
			<Text style={styles.fieldHint}>Insert the machine place, e.g. &quot;Lunchroom&quot; or &quot;Warehouse&quot;, if known.</Text>
			{
				!assignedMachine && (
					<Text style={styles.fieldHint}>To select the place, first select the machine.</Text>
				)
			}
			<Text style={styles.fieldLabel}>
			Machine
				<Text style={styles.requiredAsterisk}> *</Text>
			</Text>
			<MachineSelector
				initialValue={assignedMachine}
				value={assignedMachine}
				onChange={handleMachineChange}
				initialFilter={{ type: POS_TYPE.MACHINE }}
				hideRemoveButton={true}
				disabled={Boolean(machineSetup.movePlanId)}
			/>
			<Text style={styles.fieldLabel}>
			Route
				<Text style={styles.requiredAsterisk}> *</Text>
			</Text>
			<RouteSelector
				initialValue={machineSetup.route}
				value={machineSetup.route}
				onChange={handleRouteChange}
				hideRemoveButton={true}
			/>
			<Text style={styles.fieldHint}>Route will be assigned to the machine automatically.</Text>
			<Text style={styles.fieldLabel}>Required planogram</Text>
			<PlanogramSelector
				initialValue={machineSetup.planogram}
				value={machineSetup.planogram}
				onChange={handlePlanogramChange}
				hideRemoveButton={false}
			/>
			<Text style={styles.fieldHint}>You can optionally select a planogram for this machine that should be used.</Text>

			<Text style={styles.sectionLabel}>Required machine quality</Text>
			{
				Object.values(MACHINE_QUALITY).map(quality => (
					<SetupPickerField
						key={quality}
						label={MACHINE_QUALITY_NAME[quality]}
						value={machineSetup.machineQualityObject?.[quality]}
						onChange={value => updateMachineQuality(quality, value)}
						options={Object.values(MACHINE_SETUP_PREFERENCE_VALUE).map(value => ({
							label: MACHINE_SETUP_PREFERENCE_VALUE_NAME[value],
							value,
						}))}
					/>
				))
			}

			<Text style={styles.sectionLabel}>Required machine size</Text>
			{
				Object.values(MACHINE_SIZE).map(size => (
					<SetupPickerField
						key={size}
						label={MACHINE_SIZE_NAME[size]}
						value={machineSetup.machineSizeObject?.[size]}
						onChange={value => updateMachineSize(size, value)}
						options={Object.values(MACHINE_SETUP_PREFERENCE_VALUE).map(value => ({
							label: MACHINE_SETUP_PREFERENCE_VALUE_NAME[value],
							value,
						}))}
					/>
				))
			}

			<Text style={styles.sectionLabel}>Assignment</Text>
			<Text style={styles.fieldLabel}>
			Assignees
				<Text style={styles.requiredAsterisk}> *</Text>
			</Text>
			<SetupAssigneePicker
				assignees={machineSetup.assignees}
				onChange={handleAssigneeChange}
			/>

			<Text style={styles.sectionLabel}>Other information</Text>
			<SetupPickerField
				label="Required door angle"
				value={machineSetup.requiredDoorAngle}
				onChange={value => updateMachineSetup({ requiredDoorAngle: value })}
				options={Object.values(MACHINE_DOOR_ANGLE).map(value => ({
					label: MACHINE_DOOR_ANGLE_NAME[value],
					value,
				}))}
			/>
			<SetupPickerField
				label="Card payment only machine?"
				value={cardPaymentOnlyChoice}
				onChange={handleCardPaymentOnlyChange}
				options={MACHINE_SETUP_CARD_PAYMENT_ONLY_OPTIONS}
				hint={cardPaymentOnlyHint}
			/>
			<SetupSwitchField
				label={`${healthComplianceLabel} compliant`}
				value={machineSetup.healthCompliant}
				onChange={value => updateMachineSetup({ healthCompliant: value })}
			/>
			<SetupPickerField
				label="Requires extension lead"
				value={machineSetup.requiresExtensionLead}
				onChange={value => updateMachineSetup({ requiresExtensionLead: value })}
				options={Object.values(YES_NO_VALUE).map(value => ({
					label: YES_NO_VALUE_NAME[value],
					value,
				}))}
				hint="Note: If 'No' OR 'Unsure' are selected, during the physical machine setup phase, the user will not be shown the step to put an extension cord in the drop bin. If we become aware of this information later in the process in general, we can change this so that some user adds the information then, or something. Notify Eamonn/Simon if so."
			/>
			<SetupTextField
				label="Additional instructions"
				value={machineSetup.additionalInstructions}
				field="additionalInstructions"
				onConfirm={updateMachineSetup}
				multiline={true}
			/>

			<SetupPhotoSection
				title="Photos"
				photos={machineSetup.placePhotos || []}
				onAddPhoto={(link) => addPhoto({ link, targetField: 'placePhotos' })}
				onUpdateComment={handleUpdateMediaComment}
				addButtonLabel="Upload photo"
			/>
			<Text style={styles.photoHint}>Insert photos of the place where the machine will go.</Text>

		</SetupSection>
	);
};

export default MachineSetupInformationSection;
